import { useEffect, useState } from "react";

/**
 * Detects whether this browser currently has an Agapaé app session, so the
 * landing page can show "Sign in" instead of "Register" for a returning user.
 *
 * The app (app.agapae.gr) writes a non-sensitive presence cookie on the shared
 * parent domain (.agapae.gr) when a session starts and clears it on logout.
 * This is same-browser detection only — it does NOT prove an active/valid
 * session (the app's own guards re-route a stale session to login). When the
 * cookie is absent (incl. localhost, where the parent-domain cookie can't be
 * shared across ports) this returns false, i.e. the safe "Register" default.
 */
const SESSION_COOKIE_NAME = "agapae_session";

function readSessionFlag() {
  if (typeof document === "undefined") return false;
  return document.cookie
    .split(";")
    .some((part) => part.trim() === `${SESSION_COOKIE_NAME}=1`);
}

export default function useHasAppSession() {
  const [hasSession, setHasSession] = useState(readSessionFlag);

  useEffect(() => {
    // Re-check when the user returns to this tab — they may have logged in or
    // out in the app tab in the meantime (the cookie is the source of truth).
    const recheck = () => setHasSession(readSessionFlag());
    window.addEventListener("focus", recheck);
    document.addEventListener("visibilitychange", recheck);
    return () => {
      window.removeEventListener("focus", recheck);
      document.removeEventListener("visibilitychange", recheck);
    };
  }, []);

  return hasSession;
}
